// DSP primitives every recipe composes (the game-builder playbook's audio cards 1, 2 and 18): seeded randomness,
// envelopes, a state-variable filter, noise colours, and the finishing chain (DC block, fades, tail trim, loudness
// normalisation, a crossfaded loop seam). Pure functions over Float32Arrays: they run in Node's tests, in a worker or
// on the main thread alike, and the same seed gives the same samples.

/** A seeded uniform generator in [0, 1) (mulberry32). */
export type Rng = () => number;

/** mulberry32: a small, fast, seeded generator. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a of a string (32-bit): a cue's seed from its id. */
export function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** A variant's generator: the cue id's hash and the variant index (stable across sessions). */
export function variantRng(id: string, variant: number): Rng {
  return mulberry32((fnv1a(id) + variant * 7919 + 13) >>> 0);
}

/** Decibels to gain. */
export const dbToGain = (db: number): number => Math.pow(10, db / 20);
/** Gain to decibels (−Infinity for 0). */
export const gainToDb = (g: number): number => (g > 0 ? 20 * Math.log10(g) : -Infinity);

const smooth = (u: number): number => u * u * (3 - 2 * u);

/** An envelope: a smoothstep attack over `att` seconds, then an exponential decay with time constant `tau`. */
export function env(t: number, att: number, tau: number): number {
  if (t < 0) return 0;
  if (t < att) return smooth(t / att);
  return Math.exp(-(t - att) / tau);
}

/** A topology-preserving state-variable filter (one channel); `set` it, then `lp`, `bp` or `hp` a sample. */
export class Svf {
  private ic1 = 0;
  private ic2 = 0;
  private g = 0;
  private k = 1;
  private a1 = 0;
  private a2 = 0;
  private a3 = 0;
  constructor(private readonly sr: number) {}
  /** Cutoff (Hz, kept under 0.45 × the rate) and Q. */
  set(fc: number, q = 0.707): void {
    const f = Math.min(Math.max(fc, 10), this.sr * 0.45);
    this.g = Math.tan((Math.PI * f) / this.sr);
    this.k = 1 / Math.max(q, 0.05);
    this.a1 = 1 / (1 + this.g * (this.g + this.k));
    this.a2 = this.g * this.a1;
    this.a3 = this.g * this.a2;
  }
  // the band-pass and low-pass outputs of the last tick (no allocation per sample: this runs millions of times)
  private v1 = 0;
  private v2 = 0;
  private tick(x: number): void {
    const v3 = x - this.ic2;
    const v1 = this.a1 * this.ic1 + this.a2 * v3;
    const v2 = this.ic2 + this.a2 * this.ic1 + this.a3 * v3;
    this.ic1 = 2 * v1 - this.ic1;
    this.ic2 = 2 * v2 - this.ic2;
    this.v1 = v1;
    this.v2 = v2;
  }
  lp(x: number): number {
    this.tick(x);
    return this.v2;
  }
  bp(x: number): number {
    this.tick(x);
    return this.v1;
  }
  hp(x: number): number {
    this.tick(x);
    return x - this.k * this.v1 - this.v2;
  }
}

/** Noise colours. */
export type NoiseColour = "white" | "pink" | "brown";

/** A noise source of one colour (pink by Paul Kellet's economy filter, brown by a leaky integrator). */
export function noiseSource(rng: Rng, colour: NoiseColour): () => number {
  if (colour === "white") return () => rng() * 2 - 1;
  if (colour === "brown") {
    let b = 0;
    return () => {
      b = (b + 0.02 * (rng() * 2 - 1)) / 1.02;
      return b * 3.5;
    };
  }
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  return () => {
    const w = rng() * 2 - 1;
    b0 = 0.99765 * b0 + w * 0.099046;
    b1 = 0.963 * b1 + w * 0.2965164;
    b2 = 0.57 * b2 + w * 1.0526913;
    return (b0 + b1 + b2 + w * 0.1848) * 0.2;
  };
}

/** A smooth random control signal (cosine-interpolated value noise at `rate` Hz), in [0, 1]. */
export function valueNoise(rng: Rng, rate: number): (t: number) => number {
  const pts: number[] = [];
  return (t: number) => {
    const x = Math.max(0, t * rate);
    const i = Math.floor(x);
    while (pts.length <= i + 1) pts.push(rng());
    const u = (1 - Math.cos((x - i) * Math.PI)) / 2;
    return pts[i]! * (1 - u) + pts[i + 1]! * u;
  };
}

/** MIDI note to Hz (A4 = 69 = 440 Hz). */
export const midiHz = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);

/** Remove DC with a one-pole high-pass at about 12 Hz. */
export function dcBlock(x: Float32Array, sr: number): void {
  const r = 1 - (2 * Math.PI * 12) / sr;
  let px = 0;
  let py = 0;
  for (let i = 0; i < x.length; i++) {
    const y = x[i]! - px + r * py;
    px = x[i]!;
    py = y;
    x[i] = y;
  }
}

/** Raised-cosine fades in and out (seconds), so a clip starts and ends at zero (no clicks). */
export function fades(x: Float32Array, sr: number, fadeIn = 0.0008, fadeOut = 0.015): void {
  const ni = Math.min(x.length, Math.max(1, Math.round(fadeIn * sr)));
  const no = Math.min(x.length, Math.max(1, Math.round(fadeOut * sr)));
  for (let i = 0; i < ni; i++) x[i]! *= 0.5 - 0.5 * Math.cos((Math.PI * i) / ni);
  for (let i = 0; i < no; i++) x[x.length - 1 - i]! *= 0.5 - 0.5 * Math.cos((Math.PI * i) / no);
}

/** The peak absolute sample. */
export function peakOf(chs: readonly Float32Array[]): number {
  let p = 0;
  for (const x of chs) for (let i = 0; i < x.length; i++) p = Math.max(p, Math.abs(x[i]!));
  return p;
}

/** RMS over a window (whole clip by default). */
export function rmsOf(x: Float32Array, from = 0, to = x.length): number {
  let s = 0;
  const a = Math.max(0, from);
  const b = Math.min(x.length, to);
  for (let i = a; i < b; i++) s += x[i]! * x[i]!;
  return b > a ? Math.sqrt(s / (b - a)) : 0;
}

/** Cut the tail where the signal stays under `db` of its peak, plus 20 ms; returns the shorter copy. */
export function trimTail(chs: Float32Array[], sr: number, db = -66): Float32Array[] {
  const thr = peakOf(chs) * dbToGain(db);
  let last = 0;
  for (const x of chs) for (let i = x.length - 1; i > last; i--) if (Math.abs(x[i]!) > thr) {
    last = i;
    break;
  }
  const n = Math.min(chs[0]!.length, last + Math.round(0.02 * sr));
  return chs.map((x) => {
    const y = x.slice(0, n);
    fades(y, sr, 0, 0.01);
    return y;
  });
}

/** The engine's loudness level (linear): the loudest 50 ms window's RMS ("short", hopped by 25 ms) or the whole
 *  clip's ("long"), of the first channel on an 80 Hz high-passed copy, so rumble doesn't count as loudness. */
function levelOf(chs: readonly Float32Array[], sr: number, mode: "short" | "long"): number {
  const probe = chs[0]!.slice();
  const f = new Svf(sr);
  f.set(80, 0.707);
  for (let i = 0; i < probe.length; i++) probe[i] = f.hp(probe[i]!);
  if (mode === "long") return rmsOf(probe);
  const w = Math.max(1, Math.round(0.05 * sr));
  let level = 0;
  for (let i = 0; i < probe.length; i += Math.max(1, w >> 1)) level = Math.max(level, rmsOf(probe, i, i + w));
  return level;
}

/** A clip's loudness in dBFS, by the measure `normalise` (and so `renderSound`) levels cues with: "short" (the
 *  default; a one-shot's) the loudest 50 ms window's RMS, "long" (a loop's) the whole clip's; on an 80 Hz high-passed
 *  copy of the first channel. −Infinity for silence. Compare two cues' levels with it, or one cue before and after. */
export function loudnessOf(chs: readonly Float32Array[], sr: number, mode: "short" | "long" = "short"): number {
  return gainToDb(levelOf(chs, sr, mode));
}

/** Loudness normalisation: "short" sets the loudest 50 ms window's RMS to `targetDb`, "long" the whole clip's; the
 *  peak is then capped at `peakCap` (0.89, about −1 dBFS). Measured on an 80 Hz high-passed copy, so rumble doesn't
 *  count as loudness (`loudnessOf` reads the same measure). */
export function normalise(chs: Float32Array[], sr: number, mode: "short" | "long", targetDb: number, peakCap = 0.89): void {
  const level = levelOf(chs, sr, mode);
  if (level <= 1e-9) return;
  let g = dbToGain(targetDb) / level;
  const p = peakOf(chs) * g;
  if (p > peakCap) g *= peakCap / p;
  for (const x of chs) for (let i = 0; i < x.length; i++) x[i]! *= g;
}

/** A loop whose seam is an equal-power crossfade: the last `seconds` are folded into the start. */
export function loopSeam(x: Float32Array, sr: number, seconds: number): Float32Array {
  const n = Math.min(Math.round(seconds * sr), Math.floor(x.length / 3));
  const out = x.slice(0, x.length - n);
  for (let i = 0; i < n; i++) {
    const u = i / n;
    out[i] = out[i]! * Math.sin((u * Math.PI) / 2) + x[x.length - n + i]! * Math.cos((u * Math.PI) / 2);
  }
  return out;
}

/** Make `[start, end)` (seconds) of a sustained sample loop without a click: the loop's last `xfade` seconds become an
 *  equal-power blend into the audio just before `start`, so playback jumping from `end` back to `start` is continuous.
 *  Returns the loop points actually used (clamped inside the sample). */
export function sustainLoop(x: Float32Array, sr: number, start: number, end: number, xfade = 0.08): [number, number] {
  const n = Math.max(1, Math.round(xfade * sr));
  const s = Math.max(n, Math.round(start * sr));
  const e = Math.min(x.length - 1, Math.round(end * sr));
  if (e - s <= 2 * n) return [s / sr, e / sr];
  for (let j = 0; j < n; j++) {
    const u = (j + 1) / n;
    x[e - n + j] = x[e - n + j]! * Math.cos((u * Math.PI) / 2) + x[s - n + j]! * Math.sin((u * Math.PI) / 2);
  }
  return [s / sr, e / sr];
}

/** Zero crossings per second (a cue's register check). */
export function zeroCrossingRate(x: Float32Array, sr: number): number {
  let n = 0;
  for (let i = 1; i < x.length; i++) if ((x[i - 1]! < 0) !== (x[i]! < 0)) n++;
  return (n * sr) / Math.max(1, x.length);
}
